import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import DialogContent from '@mui/material/DialogContent'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemText from '@mui/material/ListItemText'
import { useTheme } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useDispatch } from 'react-redux'
import { toast } from 'react-toastify'

import api from 'api'
import {
  useLazyGetPaymentTermsConfigByAccountQuery,
  useLazyGetPaymentTermsHistoryByAccountQuery,
  useUpdatePaymentTermsMutation,
} from 'api/accounts/paymentTerms'
import { CACHE_TAG_ACCOUNTS_VERSION_LIST } from 'api/cacheTagTypes'
import { useLazyGetGeneralBillingQuery } from 'api/settings/getGeneralBilling'
import { ReactComponent as FrameIcon } from 'assets/FrameIcon.svg'
import CancelButton from 'components/buttons/CancelButton'
import SaveButton from 'components/buttons/SaveButton'
import { HHAlert } from 'components/common/HHAlert'
import HHBaseDialog from 'components/common/HHBaseDialog'
import HHConfirmDialog from 'components/common/HHConfirmDialog'
import HHDialogActions from 'components/common/HHDialogActions'
import HHDialogTitle from 'components/common/HHDialogTitle'
import Loader from 'components/common/loader'
import HHFormTextField from 'components/form-fields/v5/HHFormTextField'
import SelectItem from 'components/form-fields/v5/SelectItem'
import NoteUserInfo from 'components/notes/common/NoteUserInfo'
import StyledBadge from 'components/notes/common/StyledBadge'
import { getPaymentTermsOptionText, getSortedPaymentTerms } from 'data/customer/billingDetailsSelector'
import { convertDateTimeToPreferedTimezone } from 'data/dateTime/convertDateTimeToPreferedTimezone'
import { getCustomerDetails } from 'middleware/actions/customers'
import { PAYMENT_DUE_DATE_TYPE_OPTIONS, PAYMENT_DUE_DATE_TYPES } from 'settings/constants/billing'
import T from 'T'
import { handleError } from 'utils/error'
import { get } from 'utils/lodash'

import HistorySkeleton from '../billing/common/HistorySkeleton'
import InheritBillingProfileCheckbox from '../billing/common/InheritBillingProfileCheckbox'
import RegenerateDraftInvoicesDialog from '../billing/common/RegenerateDraftInvoicesDialog'
import { useRegenerateDraftInvoicesPrompt } from '../billing/common/useRegenerateDraftInvoicesPrompt'

import CreatePaymentTermsDialog from './CreatePaymentTermsDialog'
import { getDayOfMonthOptions, getPaymentDueDateText, getShortMonthClampInfo, getTriggerTypeText } from './settings'

const ChangePaymentTermsDialog = ({ isOpen = false, accountId, onClose }) => {
  const dispatch = useDispatch()
  const theme = useTheme()
  const [isHistoryExpanded, setIsHistoryExpanded] = useState(false)
  const [isOpenCreatePaymentTermsDialog, setIsOpenCreatePaymentTermsDialog] = useState(false)
  const [isOpenSuccessDialog, setIsOpenSuccessDialog] = useState(false)
  const [getGeneralBilling, { isFetching: isPaymentTermsLoading, data: paymentTermsData }] = useLazyGetGeneralBillingQuery()
  const [getPaymentTermsConfig, { isFetching: isConfigFetching, data: configData }] = useLazyGetPaymentTermsConfigByAccountQuery()
  const [updatePaymentTerms, { isLoading }] = useUpdatePaymentTermsMutation()
  const { maybePromptRegenerate, regenerateDraftDialogProps } = useRegenerateDraftInvoicesPrompt(accountId)
  const [
    getPaymentTermsHistoryByAccount,
    { isLoading: isHistoryLoading, isFetching: isHistoryFetching, data: historyData, isSuccess: isHistorySuccess },
  ] = useLazyGetPaymentTermsHistoryByAccountQuery()

  const changePaymentTermsForm = useForm({
    defaultValues: {
      paymentDueDateType: PAYMENT_DUE_DATE_TYPES.NET_DAYS,
      paymentTermsId: '',
      paymentDayOfMonth: '',
      inheritPaymentTermsOnUpdate: true,
    },
  })
  const {
    control,
    setValue,
    reset,
    handleSubmit,
    watch,
    formState: { errors, isDirty },
  } = changePaymentTermsForm

  const inheritPaymentTerms = watch('inheritPaymentTermsOnUpdate')
  const watchedPaymentDueDateType = watch('paymentDueDateType')
  const watchedPaymentDayOfMonth = watch('paymentDayOfMonth')
  const isSpecificDay = watchedPaymentDueDateType === PAYMENT_DUE_DATE_TYPES.SPECIFIC_DAY_OF_MONTH

  const billingProfilePaymentDueDateType = get(configData, 'billingProfile.paymentDueDateType', PAYMENT_DUE_DATE_TYPES.NET_DAYS)
  const billingProfilePaymentDayOfMonth = get(configData, 'billingProfile.paymentDayOfMonth', '')
  const billingProfilePaymentTermsId = get(configData, 'billingProfile.paymentTerm.id', '')
  const billingProfileName = get(configData, 'billingProfile.name', '')
  const isBillingProfileSpecificDay = billingProfilePaymentDueDateType === PAYMENT_DUE_DATE_TYPES.SPECIFIC_DAY_OF_MONTH
  const billingProfilePaymentTermTypeLabel =
    PAYMENT_DUE_DATE_TYPE_OPTIONS.find(o => o.value === billingProfilePaymentDueDateType)?.label ?? ''
  const billingProfileSpecificDayLabel = getDayOfMonthOptions().find(o => o.value === Number(billingProfilePaymentDayOfMonth))?.label ?? ''
  const billingProfileNetTermsText = getPaymentTermsOptionText(
    get(configData, 'billingProfile.paymentTerm.term', ''),
    get(configData, 'billingProfile.paymentTerm.interval', '')
  )
  const billingProfileDetailRow = isBillingProfileSpecificDay
    ? { label: T.DAY_OF_MONTH, value: billingProfileSpecificDayLabel }
    : { label: T.PAYMENT_TERMS, value: billingProfileNetTermsText }
  const paymentTermsIdHelperText = get(errors, 'paymentTermsId.message')
  const paymentDayOfMonthHelperText = get(errors, 'paymentDayOfMonth.message')
  const paymentTerms = get(paymentTermsData, 'paymentTerm', [])
  const sortedPaymentTerms = getSortedPaymentTerms(paymentTerms)
  const history = useMemo(() => get(historyData, 'history', []), [historyData])
  const hasHistory = useMemo(() => history.length !== 0 && isHistorySuccess, [history, isHistorySuccess])
  const historyLoading = useMemo(() => isHistoryLoading || isHistoryFetching, [isHistoryLoading, isHistoryFetching])

  const selectedDayLabel = useMemo(
    () => getDayOfMonthOptions().find(o => o.value === Number(watchedPaymentDayOfMonth))?.label ?? '',
    [watchedPaymentDayOfMonth]
  )
  const shortMonthClampInfo = getShortMonthClampInfo(watchedPaymentDayOfMonth)

  const handleHistoryToggle = useCallback((_event, expanded) => setIsHistoryExpanded(expanded), [])

  const handleInheritChange = useCallback(
    event => {
      const { checked } = event.target
      // shouldDirty so toggling inherit enables Save (it's a manual setValue, not a Controller field).
      setValue('inheritPaymentTermsOnUpdate', checked, { shouldDirty: true })
      if (!checked) {
        setValue('paymentTermsId', '')
        setValue('paymentDayOfMonth', '')
      }
    },
    [setValue]
  )

  const handleOpenCreatePaymentTermsDialog = useCallback(() => setIsOpenCreatePaymentTermsDialog(true), [])

  const handleCreatePaymentTermsSuccess = useCallback(id => setValue('paymentTermsId', id, { shouldDirty: true }), [setValue])

  const handleCloseCreatePaymentTermsDialog = useCallback(() => setIsOpenCreatePaymentTermsDialog(false), [])

  const handleCloseSuccessDialog = useCallback(() => setIsOpenSuccessDialog(false), [])

  const onSubmitHandler = data => {
    const isSpecificDayType = data.paymentDueDateType === PAYMENT_DUE_DATE_TYPES.SPECIFIC_DAY_OF_MONTH
    updatePaymentTerms({
      accountId,
      paymentDueDateType: data.paymentDueDateType,
      paymentTermsId: isSpecificDayType ? null : data.paymentTermsId,
      paymentDayOfMonth: isSpecificDayType ? Number(data.paymentDayOfMonth) : null,
      inheritPaymentTermsOnUpdate: data.inheritPaymentTermsOnUpdate,
    })
      .unwrap()
      .then(async () => {
        dispatch(api.util.invalidateTags([{ type: CACHE_TAG_ACCOUNTS_VERSION_LIST, id: accountId }]))
        dispatch(getCustomerDetails({ accountId }))
        onClose()
        // When the account has draft invoices we can regenerate, confirm with a toast and let the
        // prompt offer to apply the new terms. Otherwise fall back to the detailed success dialog.
        const promptedRegenerate = await maybePromptRegenerate()
        if (promptedRegenerate) toast.success(T.PAYMENT_TERMS_UPDATED_SUCCESSFULLY)
        else setIsOpenSuccessDialog(true)
      })
      .catch(handleError)
  }

  // Sync billing-profile values into the form when inherit is checked.
  // Clearing on uncheck is handled by the checkbox onChange to avoid firing on reset().
  useEffect(() => {
    if (!inheritPaymentTerms) return
    const profileType = billingProfilePaymentDueDateType ?? PAYMENT_DUE_DATE_TYPES.NET_DAYS
    setValue('paymentDueDateType', profileType)
    if (profileType === PAYMENT_DUE_DATE_TYPES.SPECIFIC_DAY_OF_MONTH) {
      setValue('paymentDayOfMonth', billingProfilePaymentDayOfMonth ?? '')
      setValue('paymentTermsId', '')
    } else {
      setValue('paymentTermsId', billingProfilePaymentTermsId)
      setValue('paymentDayOfMonth', '')
    }
  }, [inheritPaymentTerms, billingProfilePaymentDueDateType, billingProfilePaymentTermsId, billingProfilePaymentDayOfMonth, setValue])

  useEffect(() => {
    if (isOpen && accountId) {
      setIsHistoryExpanded(false)
      getPaymentTermsConfig({ accountId }).unwrap().catch(handleError)
      getGeneralBilling().unwrap().catch(handleError)
      getPaymentTermsHistoryByAccount({ accountId }).unwrap().catch(handleError)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, accountId])

  // Seed the form once the fresh config arrives.
  useEffect(() => {
    if (!isOpen || !configData) return
    const dueDateType = get(configData, 'paymentDueDateType') ?? PAYMENT_DUE_DATE_TYPES.NET_DAYS
    const isSpecificDueDate = dueDateType === PAYMENT_DUE_DATE_TYPES.SPECIFIC_DAY_OF_MONTH
    reset({
      paymentDueDateType: dueDateType,
      paymentTermsId: isSpecificDueDate ? '' : get(configData, 'paymentTerms.id', ''),
      paymentDayOfMonth: isSpecificDueDate ? get(configData, 'paymentDayOfMonth', '') ?? '' : '',
      inheritPaymentTermsOnUpdate: get(configData, 'inheritPaymentTermsFromBillingProfile', false),
    })
  }, [isOpen, configData, reset])

  return (
    <>
      {(isPaymentTermsLoading || isConfigFetching) && <Loader />}
      <HHBaseDialog fullWidth maxWidth="xs" open={isOpen} onClose={onClose}>
        <HHDialogTitle title={T.CHANGE_PAYMENT_TERMS} onClose={onClose} />
        <DialogContent>
          <FormProvider {...changePaymentTermsForm}>
            <Box display="flex" flexDirection="column" gap={1} mt={2}>
              {/* Payment term type dropdown */}
              <HHFormTextField
                fullWidth
                select
                control={control}
                deprecatedLabel={false}
                disabled={inheritPaymentTerms}
                label={T.PAYMENT_TERM_TYPE}
                name="paymentDueDateType"
              >
                {PAYMENT_DUE_DATE_TYPE_OPTIONS.map(({ value, label }) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </HHFormTextField>

              {/* Conditional: Net terms dropdown OR day-of-month dropdown */}
              {!isSpecificDay ? (
                <HHFormTextField
                  fullWidth
                  select
                  control={control}
                  deprecatedLabel={false}
                  disabled={inheritPaymentTerms}
                  error={paymentTermsIdHelperText}
                  helperText={paymentTermsIdHelperText}
                  label={T.SELECT_PAYMENT_TERMS_LABEL}
                  name="paymentTermsId"
                  required={!inheritPaymentTerms}
                  rules={inheritPaymentTerms ? {} : { required: T.CANNOT_BE_EMPTY }}
                >
                  {sortedPaymentTerms.map(({ id, term, interval }) => (
                    <SelectItem key={id} value={id}>{`${getPaymentTermsOptionText(term, interval)}`}</SelectItem>
                  ))}
                </HHFormTextField>
              ) : (
                <Box>
                  <HHFormTextField
                    fullWidth
                    select
                    control={control}
                    deprecatedLabel={false}
                    disabled={inheritPaymentTerms}
                    error={paymentDayOfMonthHelperText}
                    helperText={paymentDayOfMonthHelperText}
                    label={T.DAY_OF_MONTH}
                    name="paymentDayOfMonth"
                    required={!inheritPaymentTerms}
                    rules={inheritPaymentTerms ? {} : { required: T.CANNOT_BE_EMPTY }}
                  >
                    {getDayOfMonthOptions().map(({ value, label }) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    ))}
                  </HHFormTextField>
                  {shortMonthClampInfo && (
                    <HHAlert borderColor={theme.palette.info.light} severity="info" sx={{ mt: 1 }}>
                      <Typography variant="body2">{shortMonthClampInfo}</Typography>
                    </HHAlert>
                  )}
                  {selectedDayLabel && (
                    <Typography color="text.secondary" sx={{ mt: 1 }} variant="body2">
                      {`${T.INVOICES_DUE_ON_DAY} ${selectedDayLabel} ${T.INVOICES_DUE_ON_DAY_SUFFIX}`}
                    </Typography>
                  )}
                </Box>
              )}

              {/* Inherit checkbox with expandable billing-profile details */}
              <InheritBillingProfileCheckbox
                billingProfileName={billingProfileName}
                checked={inheritPaymentTerms}
                onChange={handleInheritChange}
              >
                <Typography color="text.secondary" variant="body2">
                  {T.PAYMENT_TERM_TYPE}
                  {': '}
                  <strong>{billingProfilePaymentTermTypeLabel}</strong>
                </Typography>
                {billingProfileDetailRow.value && (
                  <Typography color="text.secondary" variant="body2">
                    {billingProfileDetailRow.label}
                    {': '}
                    <strong>{billingProfileDetailRow.value}</strong>
                  </Typography>
                )}
              </InheritBillingProfileCheckbox>
            </Box>
          </FormProvider>

          {/* History accordion */}
          <Accordion
            disableGutters
            elevation={0}
            expanded={isHistoryExpanded}
            sx={{ mt: 2, '&::before': { display: 'none' } }}
            onChange={handleHistoryToggle}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                px: 0,
                flexDirection: 'row-reverse',
                '& .MuiAccordionSummary-expandIconWrapper': { mr: 1 },
                '& .MuiAccordionSummary-content': { justifyContent: 'space-between', alignItems: 'center' },
              }}
            >
              <Typography fontWeight={400} variant="h6">
                {T.HISTORY}
              </Typography>
              {hasHistory && (
                <StyledBadge badgeContent={history.length}>
                  <FrameIcon />
                </StyledBadge>
              )}
            </AccordionSummary>
            <AccordionDetails sx={{ px: 0 }}>
              <List disablePadding>
                {historyLoading && <HistorySkeleton rows={4} />}
                {!historyLoading && !hasHistory && (
                  <HHAlert borderColor={theme.palette.info.light} severity="info">
                    No payment terms history is currently available.
                  </HHAlert>
                )}
                {!historyLoading &&
                  hasHistory &&
                  history.map(
                    (
                      {
                        createdAt,
                        createdByUser,
                        terms = '',
                        interval = '',
                        triggerType,
                        id,
                        paymentDueDateType: historyType,
                        paymentDayOfMonth: historyDay,
                        inheritPaymentTermsFromBillingProfile: historyInherit,
                      },
                      index
                    ) => {
                      const { firstName = '', lastName = '' } = createdByUser || {}
                      const { onlyDate = '', onlyTime = '' } = convertDateTimeToPreferedTimezone({ dateTime: createdAt }) || {}
                      const triggerText = getTriggerTypeText(triggerType)
                      const isLastRow = index === history.length - 1
                      const userName = firstName || lastName ? `${firstName} ${lastName}`.trim() : T.HAULER_HERO_SYSTEM
                      const dateTimeText = onlyDate && onlyTime ? `${onlyDate} @${onlyTime}` : ''
                      const paymentDueDateDisplayText = getPaymentDueDateText(historyType, historyDay, terms, interval)
                      const primaryText = triggerText
                        ? `Changed to ${paymentDueDateDisplayText} ${triggerText}`
                        : `Changed to ${paymentDueDateDisplayText}`
                      const inheritText = historyInherit == null ? '' : `Inherit from profile: ${historyInherit ? 'On' : 'Off'}`
                      const secondaryText = [`Updated by ${userName}`, dateTimeText].filter(Boolean).join(' | ')

                      return (
                        <ListItem key={`${createdAt}-${id}`} divider={!isLastRow}>
                          <NoteUserInfo firstName={firstName} lastName={lastName} />
                          <ListItemText
                            primary={primaryText}
                            secondary={
                              <>
                                {inheritText && (
                                  <Typography color="text.primary" component="span" display="block" variant="body2">
                                    {inheritText}
                                  </Typography>
                                )}
                                {secondaryText}
                              </>
                            }
                            slotProps={{
                              primary: { variant: 'h6', fontWeight: 400 },
                            }}
                          />
                        </ListItem>
                      )
                    }
                  )}
              </List>
            </AccordionDetails>
          </Accordion>
        </DialogContent>
        <HHDialogActions>
          <CancelButton size="medium" onClick={onClose} />
          <Box sx={{ flex: '1 1 auto' }} />
          <Button
            disabled={inheritPaymentTerms || isSpecificDay}
            size="small"
            variant="outlined"
            onClick={handleOpenCreatePaymentTermsDialog}
          >
            {T.CREATE_NEW}
          </Button>
          <SaveButton disabled={!isDirty} loading={isLoading} onClick={handleSubmit(onSubmitHandler)} />
        </HHDialogActions>
      </HHBaseDialog>
      <HHConfirmDialog
        ConfirmButtonProps={{ color: 'primary' }}
        confirmButtonTitle={T.OK}
        confirmDescription={T.PAYMENT_TERMS_UPDATED_DETAILED}
        confirmTitle={T.PAYMENT_TERMS_UPDATED_TITLE}
        DialogProps={{ maxWidth: 'xs', fullWidth: true }}
        isOpen={isOpenSuccessDialog}
        showCancelButton={false}
        onClose={handleCloseSuccessDialog}
        onConfirm={handleCloseSuccessDialog}
      />
      <RegenerateDraftInvoicesDialog {...regenerateDraftDialogProps} />
      <CreatePaymentTermsDialog
        isOpen={isOpenCreatePaymentTermsDialog}
        onClose={handleCloseCreatePaymentTermsDialog}
        onSuccess={handleCreatePaymentTermsSuccess}
      />
    </>
  )
}

ChangePaymentTermsDialog.propTypes = {
  isOpen: PropTypes.bool,
  accountId: PropTypes.string,
  onClose: PropTypes.func.isRequired,
}

export default ChangePaymentTermsDialog
